import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import Navbar from '../components/Navbar';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  Grid, 
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Avatar,
  Divider,
  Paper
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  School as SchoolIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { deepPurple } from '@mui/material/colors';
import api from '../services/api';

const UserSettings = () => {
  const [user, setUser] = useState({
    studentId: '',
    name: '',
    role: '',
    email: '',
    program: '',
    password: '••••••••',
    newPassword: '',
    confirmPassword: '',
  });

  const [editing, setEditing] = useState({
    program: false,
    password: false,
  });

  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });

  const [tempValues, setTempValues] = useState({
    program: user.program,
  });

  const [passwordError, setPasswordError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userId = localStorage.getItem('user_id');
        if (!userId) return;
        const res = await api.get(`/users/${userId}`);
        let details = {};
        try {
          details = JSON.parse(res.data.user_details || '{}');
        } catch {}
        setUser({
          studentId: details.student_id || '',
          name: details.name || '',
          role: res.data.role,
          email: res.data.email,
          program: details.program || '',
          password: '••••••••',
          newPassword: '',
          confirmPassword: '',
        });
      } catch (err) {
        // Optionally set an error state here
      }
    };
    fetchUser();
  }, []);

  const handleChange = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const handleTempChange = (e) => {
    setTempValues({ ...tempValues, [e.target.name]: e.target.value });
  };

  const handleEdit = (field) => {
    setEditing({ ...editing, [field]: true });
    if (field === 'password') {
      setUser({ ...user, newPassword: '', confirmPassword: '' });
    } else {
      setTempValues({ ...tempValues, [field]: user[field] });
    }
  };

  const handleSave = (field) => {
    if (field === 'password') {
      handlePasswordReset();
    } else {
      setUser({ ...user, [field]: tempValues[field] });
      setEditing({ ...editing, [field]: false });
      showSuccessMessage('Program updated successfully!');
    }
  };

  const handleCancel = (field) => {
    setEditing({ ...editing, [field]: false });
    if (field === 'password') {
      setUser({ ...user, newPassword: '', confirmPassword: '' });
      setPasswordError('');
    }
  };

  const handleClickShowPassword = (field) => {
    setShowPassword({ ...showPassword, [field]: !showPassword[field] });
  };

  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handlePasswordReset = async () => {
    const { newPassword, confirmPassword } = user;

    if (!newPassword || !confirmPassword) {
      setPasswordError("Please fill out both password fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match!");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("Password must be at least 8 characters!");
      return;
    }

    try {
      // Get user_id and token from localStorage
      const userId = localStorage.getItem('user_id');
      const token = localStorage.getItem('token');
      if (!userId || !token) {
        setPasswordError('Not authenticated. Please log in again.');
        return;
      }
      await api.post(`/users/${userId}/change-password`, { new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setPasswordError('');
      setUser({ 
        ...user, 
        password: '••••••••',
        newPassword: '',
        confirmPassword: '' 
      });
      setEditing({ ...editing, password: false });
      showSuccessMessage('Password updated successfully!');
    } catch (err) {
      setPasswordError(err.response?.data?.error || "Failed to update password");
    }
  };

  return (
    <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            position: 'relative' // Added for proper footer positioning
          }}>
            <Navbar/>
    <Container maxWidth="md" sx={{ pt:10,
          mt: 4, 
          mb: 6,
          flex: 1, // Takes up all available space
          pb: 8  }}>
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Box 
          sx={{ 
            backgroundColor: deepPurple[500], 
            color: 'white', 
            p: 3,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Avatar sx={{ bgcolor: deepPurple[300], mr: 2 }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="bold">User Settings</Typography>
            <Typography variant="subtitle2">Manage your account information</Typography>
          </Box>
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={4}>
            {/* User Information Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: deepPurple[700]
              }}>
                <BadgeIcon sx={{ mr: 1 }} /> Basic Information
              </Typography>
              
              <Divider sx={{ mb: 3 }} />

              {/* Student ID */}
              <TextField
                fullWidth
                label="Student ID"
                value={user.studentId}
                margin="normal"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <BadgeIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* User Name */}
              <TextField
                fullWidth
                label="Name"
                value={user.name}
                margin="normal"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              {/* User Role */}
              <TextField
                fullWidth
                label="Role"
                value={user.role}
                margin="normal"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Email */}
              <TextField
                fullWidth
                label="Email"
                value={user.email}
                margin="normal"
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Editable Fields Section */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom sx={{ 
                display: 'flex', 
                alignItems: 'center',
                color: deepPurple[700]
              }}>
                <SchoolIcon sx={{ mr: 1 }} /> Academic Information
              </Typography>
              
              <Divider sx={{ mb: 3 }} />

              {/* Program */}
              <TextField
                fullWidth
                label="Program"
                name="program"
                value={editing.program ? tempValues.program : user.program}
                onChange={handleTempChange}
                margin="normal"
                InputProps={{
                  readOnly: !editing.program,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SchoolIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {editing.program ? (
                        <>
                          <IconButton onClick={() => handleSave('program')} size="small">
                            <CheckIcon color="success" />
                          </IconButton>
                          <IconButton onClick={() => handleCancel('program')} size="small">
                            <CancelIcon color="error" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton onClick={() => handleEdit('program')} size="small">
                          <EditIcon color="primary" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />

              {/* Password Section */}
              <Box mt={4}>
                <Typography variant="h6" gutterBottom sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: deepPurple[700]
                }}>
                  <LockIcon sx={{ mr: 1 }} /> Password Management
                </Typography>
                
                <Divider sx={{ mb: 3 }} />

                {!editing.password ? (
                  <TextField
                    fullWidth
                    label="Password"
                    type="text"
                    value={user.password}
                    margin="normal"
                    InputProps={{
                      readOnly: true,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleEdit('password')} size="small">
                            <EditIcon color="primary" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                ) : (
                  <>
                    {/* New Password */}
                    <TextField
                      fullWidth
                      label="New Password"
                      type={showPassword.newPassword ? 'text' : 'password'}
                      name="newPassword"
                      value={user.newPassword}
                      onChange={handleChange}
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleClickShowPassword('newPassword')}
                              size="small"
                            >
                              {showPassword.newPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Confirm Password */}
                    <TextField
                      fullWidth
                      label="Confirm Password"
                      type={showPassword.confirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={user.confirmPassword}
                      onChange={handleChange}
                      margin="normal"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => handleClickShowPassword('confirmPassword')}
                              size="small"
                            >
                              {showPassword.confirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />

                    {/* Error Message */}
                    {passwordError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {passwordError}
                      </Alert>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleCancel('password')}
                        sx={{ mr: 2 }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => handleSave('password')}
                        disabled={!user.newPassword || !user.confirmPassword}
                      >
                        Update Password
                      </Button>
                    </Box>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Success Message */}
          {successMessage && (
            <Alert severity="success" sx={{ mt: 3 }}>
              {successMessage}
            </Alert>
          )}
        </CardContent>
      </Paper>
    </Container>
     <Box sx={{ 
      width: '100%',
      bgcolor: 'background.paper',
      boxShadow: '0 -2px 5px rgba(0,0,0,0.1)',
      position: 'relative', // Changed from fixed to relative
      mt: 'auto' // Pushes footer to bottom
    }}>
      <Footer />
    </Box>
  </Box>
  );
};

export default UserSettings;